import { Fragment } from "react";
import { CheckIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import {
  getCurrentStatus,
  getOrderStatusOption,
  type OrderDetailStep,
} from "../../../orders/components/statusSelector/orderSteps";

type StatusCardProps = {
  orderId: string;
  steps: OrderDetailStep[];
};

export default function StatusCard({ orderId, steps }: StatusCardProps) {
  const { currentIndex, note } = getCurrentStatus(steps);

  return (
    <div className="flex flex-col gap-6 md:gap-8 rounded-xl border border-gray-100 bg-white p-4 md:p-5">
      <div className="flex flex-col gap-2">
        <span className="text-sm text-gray-500">Order ID</span>
        <span className="text-base md:text-lg font-semibold text-gray-950">
          {orderId}
        </span>
      </div>

      <div className="md:overflow-x-auto">
        <ol className="flex flex-col md:min-w-lg md:flex-row md:items-start w-full">
          {steps.map((stepData, index) => {
            const stepOption = getOrderStatusOption(stepData.step);
            const isDone = index < currentIndex;
            const isCurrent = index === currentIndex;
            const StepIcon = stepOption.icon;

            return (
              <Fragment key={stepData.step}>
                {index > 0 && (
                  <li
                    aria-hidden
                    className={clsx(
                      "ml-3.75 my-2 h-8 w-0.5 rounded-full md:ml-0 md:mx-2 md:mt-5 md:h-0.5 md:flex-1",
                      index <= currentIndex ? "bg-blue-600" : "bg-gray-200",
                    )}
                  />
                )}
                <li className="flex shrink-0 items-center gap-3 md:flex-col md:flex-1 md:min-w-0">
                  <div
                    className={clsx(
                      "flex size-8 md:size-10 shrink-0 items-center justify-center rounded-full",
                      isDone && "bg-blue-600 text-white",
                      isCurrent &&
                        "bg-[#F9814D] text-white ring-4 ring-[#FFF9F0]",
                      !isDone &&
                        !isCurrent &&
                        "border border-gray-300 bg-white text-gray-400",
                    )}
                  >
                    {isDone ? (
                      <CheckIcon className="size-4 md:size-5 stroke-2" />
                    ) : (
                      StepIcon && <StepIcon className="size-4 md:size-5" />
                    )}
                  </div>
                  <span
                    className={clsx(
                      "whitespace-nowrap md:whitespace-normal text-xs md:text-sm md:text-center",
                      isDone || isCurrent ? "text-gray-950" : "text-gray-400",
                    )}
                  >
                    {stepOption.label}
                  </span>
                </li>
              </Fragment>
            );
          })}
        </ol>
      </div>

      {note && (
        <p className="flex items-start gap-2 rounded-lg bg-[#F9FAFB] px-3 py-2.5 text-xs md:text-sm text-gray-500">
          <InformationCircleIcon className="size-4 shrink-0 mt-0.5" />
          {note}
        </p>
      )}
    </div>
  );
}
